import { jsPDF } from "jspdf";

// Colors
const COLORS = {
    primary: [139, 92, 246],      // Violet
    secondary: [6, 182, 212],     // Cyan
    dark: [10, 10, 10],           // Near black
    text: [30, 30, 30],           // Dark gray
    textLight: [100, 100, 100],   // Medium gray
    success: [16, 185, 129],      // Green
    warning: [245, 158, 11],      // Amber
    danger: [239, 68, 68],        // Red
    white: [255, 255, 255],
    lightBg: [248, 250, 252]      // Light gray bg
};

// Question details for the PDF
const QUESTION_DETAILS = {
    email_capture: {
        title: "No Email Capture System",
        whyMatters: "Without email capture, 90-98% of visitors leave and never return. You're losing your chance to nurture potential customers and build long-term relationships.",
        revenueImpact: "Potential loss: 15-25% repeat sales opportunity",
        recommendedFixes: [
            "Add exit intent popup with compelling offer",
            "Create a valuable lead magnet (guide, checklist, discount)",
            "Integrate email platform (Klaviyo, MailerLite, ConvertKit)",
            "Set up welcome email sequence"
        ],
        difficulty: "easy",
        priority: 1,
        weekAssignment: 1
    },
    abandoned_cart: {
        title: "Missing Abandoned Cart Recovery",
        whyMatters: "70% of online shopping carts are abandoned. Without recovery emails, you're leaving money on the table from customers who were ready to buy.",
        revenueImpact: "Potential loss: 10-30% of would-be sales",
        recommendedFixes: [
            "Enable abandoned cart email sequence (3-email series)",
            "Offer incentive in second email (discount, free shipping)",
            "Add urgency messaging ('Items selling fast')",
            "Include product images and easy return-to-cart link"
        ],
        difficulty: "easy",
        priority: 2,
        weekAssignment: 1
    },
    upsells_bundles: {
        title: "No Upsells or Product Bundles",
        whyMatters: "Upselling to existing customers is 5-25x cheaper than acquiring new ones. Bundles increase average order value by 20-35% on average.",
        revenueImpact: "Potential loss: 20-35% higher order values",
        recommendedFixes: [
            "Add 'Frequently Bought Together' recommendations",
            "Create product bundles at 10-15% discount",
            "Implement one-click upsells post-purchase",
            "Add cross-sell suggestions on cart page"
        ],
        difficulty: "moderate",
        priority: 5,
        weekAssignment: 3
    },
    customer_reviews: {
        title: "Missing Customer Reviews",
        whyMatters: "93% of consumers say online reviews impact their purchasing decisions. Products with reviews convert 270% better than those without.",
        revenueImpact: "Potential loss: 15-25% conversion rate",
        recommendedFixes: [
            "Install review collection app (Judge.me, Loox, Yotpo)",
            "Send post-purchase review request emails",
            "Display star ratings on product pages",
            "Respond to reviews (builds trust)"
        ],
        difficulty: "easy",
        priority: 3,
        weekAssignment: 2
    },
    cta_above_fold: {
        title: "Weak Above-the-Fold CTA",
        whyMatters: "Users spend 80% of their time above the fold. If your call-to-action isn't immediately visible, you're losing impulse buyers.",
        revenueImpact: "Potential loss: 10-20% immediate conversions",
        recommendedFixes: [
            "Place primary CTA button above the fold",
            "Use action-oriented text ('Get Started', 'Shop Now')",
            "Add contrasting button color for visibility",
            "Include supporting value proposition nearby"
        ],
        difficulty: "easy",
        priority: 4,
        weekAssignment: 2
    },
    mobile_optimized: {
        title: "Poor Mobile Experience",
        whyMatters: "60%+ of traffic is mobile. A non-optimized mobile site can see 50% higher bounce rates and significantly lower conversions.",
        revenueImpact: "Potential loss: 30-50% of mobile revenue",
        recommendedFixes: [
            "Test site on multiple mobile devices",
            "Ensure buttons are thumb-friendly (min 44px)",
            "Simplify navigation for mobile users",
            "Optimize images for faster mobile loading"
        ],
        difficulty: "moderate",
        priority: 6,
        weekAssignment: 4
    },
    fast_loading: {
        title: "Slow Page Load Speed",
        whyMatters: "A 1-second delay in page load time can reduce conversions by 7%. 53% of mobile users abandon sites that take over 3 seconds to load.",
        revenueImpact: "Potential loss: 7-20% per second of delay",
        recommendedFixes: [
            "Compress and optimize all images",
            "Enable browser caching",
            "Minimize JavaScript and CSS files",
            "Use a content delivery network (CDN)"
        ],
        difficulty: "advanced",
        priority: 7,
        weekAssignment: 4
    }
};

// Helper to add page number
const addPageNumber = (doc, pageNum) => {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.text(`${pageNum}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
};

// Helper to add footer
const addFooter = (doc) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.text("Generated by SiteGradePro.com", doc.internal.pageSize.getWidth() / 2, pageHeight - 5, { align: 'center' });
};

// Helper to add header with logo on pages (except cover and closing)
const addHeader = (doc, logoImg) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    
    // Left side: SiteGradePro text
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", margin, 12);
    
    // Right side: Logo image (if loaded)
    if (logoImg) {
        try {
            doc.addImage(logoImg, 'PNG', pageWidth - margin - 25, 5, 25, 12);
        } catch (e) {
            // Fallback to text if image fails
            doc.text("SiteGradePro", pageWidth - margin - 25, 12);
        }
    }
    
    // Divider line
    doc.setDrawColor(...COLORS.textLight);
    doc.setLineWidth(0.3);
    doc.line(margin, 18, pageWidth - margin, 18);
};

// Helper to draw difficulty badge
const drawDifficultyBadge = (doc, difficulty, x, y) => {
    const badges = {
        easy: { text: "EASY", color: COLORS.success },
        moderate: { text: "MODERATE", color: COLORS.warning },
        advanced: { text: "ADVANCED", color: COLORS.danger }
    };
    const badge = badges[difficulty];
    
    doc.setFillColor(...badge.color);
    doc.roundedRect(x, y, 25, 6, 1, 1, 'F');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text(badge.text, x + 12.5, y + 4.2, { align: 'center' });
};

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

// Main PDF Generator
export async function generateProfitLeakFixPlanPDF(data) {
    const {
        businessName = "Your Business",
        websiteUrl = "yourwebsite.com",
        answers = {},
        score = 0,
        maxScore = 70,
        leadData = null
    } = data;

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let pageNum = 1;
    
    // Try to load logo image
    let logoImg = null;
    try {
        const response = await fetch(LOGO_URL);
        const blob = await response.blob();
        logoImg = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.log("Could not load logo for PDF");
    }

    // Calculate leaks (questions answered "No")
    const leaks = Object.entries(answers)
        .filter(([key, value]) => value === false)
        .map(([key]) => ({ id: key, ...QUESTION_DETAILS[key] }))
        .sort((a, b) => a.priority - b.priority);

    const leakCount = leaks.length;
    const scorePercent = Math.round((score / maxScore) * 100);

    // Estimated revenue leakage calculation
    const minLeakage = leakCount * 150;
    const maxLeakage = leakCount * 500;

    // ========== PAGE 1: COVER PAGE ==========
    // Dark gradient header
    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 100, 'F');
    
    // Accent line
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 98, pageWidth, 3, 'F');

    // Logo text
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", margin, 30);

    // Main title
    doc.setFontSize(32);
    doc.setTextColor(...COLORS.white);
    doc.text("Profit Leak", margin, 55);
    doc.setTextColor(...COLORS.secondary);
    doc.text("Fix Plan", margin, 70);
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.white);
    doc.text("TM", margin + 62, 62);

    // Report details box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, 115, contentWidth, 50, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text("CUSTOM OPTIMIZATION REPORT FOR:", margin + 10, 128);
    
    doc.setFontSize(18);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text(leadData?.name || businessName, margin + 10, 142);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.primary);
    doc.text(leadData?.website || websiteUrl, margin + 10, 152);
    
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text(new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
    }), margin + 10, 160);

    // Score preview on cover
    const scoreX = pageWidth - margin - 50;
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(scoreX - 5, 115, 60, 50, 3, 3, 'F');
    doc.setDrawColor(...COLORS.primary);
    doc.setLineWidth(0.5);
    doc.roundedRect(scoreX - 5, 115, 60, 50, 3, 3, 'S');
    
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.text("REVENUE SCORE", scoreX + 25, 128, { align: 'center' });
    
    const scoreColor = score >= 50 ? COLORS.success : score >= 30 ? COLORS.warning : COLORS.danger;
    doc.setFontSize(28);
    doc.setTextColor(...scoreColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`${score}`, scoreX + 25, 148, { align: 'center' });
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.textLight);
    doc.text(`/ ${maxScore}`, scoreX + 25, 158, { align: 'center' });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 2: EXECUTIVE SUMMARY ==========
    doc.addPage();
    pageNum++;

    // Header bar
    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("EXECUTIVE SUMMARY", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    let yPos = 40;

    // Score display section
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Your Revenue Readiness Score", margin, yPos);
    yPos += 15;

    // Large score circle
    const circleX = margin + 30;
    const circleY = yPos + 20;
    doc.setFillColor(248, 250, 252);
    doc.circle(circleX, circleY, 25, 'F');
    doc.setDrawColor(...scoreColor);
    doc.setLineWidth(3);
    doc.circle(circleX, circleY, 25, 'S');
    
    doc.setFontSize(24);
    doc.setTextColor(...scoreColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`${score}`, circleX, circleY + 3, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`/ ${maxScore}`, circleX, circleY + 12, { align: 'center' });

    // Risk level indicator
    const riskX = circleX + 50;
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'normal');
    doc.text("Revenue Risk Level:", riskX, circleY - 15);
    
    // Risk badges
    const riskLevels = [
        { label: "Critical", color: COLORS.danger, active: score < 30 },
        { label: "Moderate", color: COLORS.warning, active: score >= 30 && score < 50 },
        { label: "Optimized", color: COLORS.success, active: score >= 50 }
    ];
    
    let badgeX = riskX;
    riskLevels.forEach((level, i) => {
        if (level.active) {
            doc.setFillColor(...level.color);
        } else {
            doc.setFillColor(220, 220, 220);
        }
        doc.roundedRect(badgeX, circleY - 8, 30, 8, 2, 2, 'F');
        doc.setFontSize(7);
        doc.setTextColor(level.active ? 255 : 150, level.active ? 255 : 150, level.active ? 255 : 150);
        doc.text(level.label, badgeX + 15, circleY - 2.5, { align: 'center' });
        badgeX += 35;
    });

    yPos = circleY + 40;

    // Plain English Summary box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, yPos, contentWidth, 45, 3, 3, 'F');
    
    yPos += 8;
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("What This Means For Your Business", margin + 8, yPos);
    
    yPos += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.text);
    
    let summaryText;
    if (leakCount === 0) {
        summaryText = "Excellent! Your website has all core revenue systems in place. This report will help you fine-tune and maximize your existing setup.";
    } else if (leakCount <= 2) {
        summaryText = `Your website is missing ${leakCount} revenue optimization ${leakCount === 1 ? 'system' : 'systems'}. Addressing ${leakCount === 1 ? 'this gap' : 'these gaps'} could improve your monthly revenue by 10-20%.`;
    } else if (leakCount <= 4) {
        summaryText = `Your website is missing ${leakCount} core revenue systems that directly impact conversion, retention, and average order value. Based on your responses, you are likely losing 15-30% of potential monthly revenue.`;
    } else {
        summaryText = `Critical: Your website is missing ${leakCount} essential revenue systems. This means you are likely losing 25-40% of potential monthly revenue. Immediate action is strongly recommended.`;
    }
    
    const summaryLines = doc.splitTextToSize(summaryText, contentWidth - 16);
    doc.text(summaryLines, margin + 8, yPos);

    yPos += summaryLines.length * 5 + 20;

    // Estimated Revenue Leakage
    if (leakCount > 0) {
        doc.setFillColor(...COLORS.danger);
        doc.setGlobalAlpha && doc.setGlobalAlpha(0.1);
        doc.roundedRect(margin, yPos, contentWidth, 30, 3, 3, 'F');
        doc.setGlobalAlpha && doc.setGlobalAlpha(1);
        doc.setDrawColor(...COLORS.danger);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, yPos, contentWidth, 30, 3, 3, 'S');
        
        yPos += 10;
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.danger);
        doc.setFont('helvetica', 'bold');
        doc.text("ESTIMATED REVENUE LEAKAGE RANGE:", margin + 8, yPos);
        
        yPos += 10;
        doc.setFontSize(18);
        doc.text(`$${minLeakage.toLocaleString()} - $${maxLeakage.toLocaleString()} per month`, margin + 8, yPos);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.textLight);
        doc.text("Based on industry averages for businesses with similar optimization gaps", margin + 8, yPos + 8);
    }

    yPos += 50;

    // Key findings summary
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Key Findings", margin, yPos);
    
    yPos += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const findings = [
        `${leakCount} of 7 revenue optimization systems need attention`,
        `Highest priority fix: ${leaks[0]?.title || 'N/A'}`,
        `Estimated implementation time: ${leakCount * 2}-${leakCount * 5} hours`,
        `Expected ROI timeline: 2-4 weeks after implementation`
    ];
    
    findings.forEach((finding, i) => {
        doc.setFillColor(...COLORS.primary);
        doc.circle(margin + 3, yPos + 1.5, 1.5, 'F');
        doc.setTextColor(...COLORS.text);
        doc.text(finding, margin + 10, yPos + 3);
        yPos += 8;
    });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGES 3-N: REVENUE LEAK BREAKDOWN ==========
    if (leaks.length > 0) {
        doc.addPage();
        pageNum++;

        // Section header
        doc.setFillColor(...COLORS.dark);
        doc.rect(0, 0, pageWidth, 25, 'F');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.text("REVENUE LEAK BREAKDOWN", margin, 16);
        // Add logo to header
        if (logoImg) {
            try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
        }

        yPos = 35;

        leaks.forEach((leak, index) => {
            // Check if we need a new page
            if (yPos > pageHeight - 80) {
                addFooter(doc);
                addPageNumber(doc, pageNum);
                doc.addPage();
                pageNum++;
                yPos = 25;
            }

            // Leak card
            doc.setFillColor(248, 250, 252);
            doc.roundedRect(margin, yPos, contentWidth, 70, 3, 3, 'F');
            doc.setDrawColor(...COLORS.danger);
            doc.setLineWidth(0.3);
            doc.roundedRect(margin, yPos, contentWidth, 70, 3, 3, 'S');

            // Leak number badge
            doc.setFillColor(...COLORS.danger);
            doc.roundedRect(margin + 5, yPos + 5, 20, 8, 2, 2, 'F');
            doc.setFontSize(7);
            doc.setTextColor(...COLORS.white);
            doc.setFont('helvetica', 'bold');
            doc.text(`LEAK #${index + 1}`, margin + 15, yPos + 10.5, { align: 'center' });

            // Title
            doc.setFontSize(12);
            doc.setTextColor(...COLORS.text);
            doc.text(leak.title, margin + 30, yPos + 11);

            // Difficulty badge
            drawDifficultyBadge(doc, leak.difficulty, pageWidth - margin - 30, yPos + 5);

            // Why it matters
            let cardY = yPos + 18;
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.textLight);
            doc.setFont('helvetica', 'bold');
            doc.text("WHY THIS MATTERS", margin + 8, cardY);
            cardY += 5;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.text);
            const whyLines = doc.splitTextToSize(leak.whyMatters, contentWidth - 16);
            doc.text(whyLines.slice(0, 2), margin + 8, cardY);
            cardY += 10;

            // Revenue impact
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.danger);
            doc.setFont('helvetica', 'bold');
            doc.text("REVENUE IMPACT: ", margin + 8, cardY);
            doc.setFont('helvetica', 'normal');
            doc.text(leak.revenueImpact, margin + 38, cardY);
            cardY += 8;

            // Quick fixes (first 2)
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.textLight);
            doc.setFont('helvetica', 'bold');
            doc.text("QUICK FIXES:", margin + 8, cardY);
            cardY += 5;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...COLORS.text);
            leak.recommendedFixes.slice(0, 2).forEach((fix, i) => {
                doc.text(`${i + 1}. ${fix}`, margin + 8, cardY);
                cardY += 4;
            });

            yPos += 78;
        });

        addFooter(doc);
        addPageNumber(doc, pageNum);
    }

    // ========== PRIORITY FIX ORDER PAGE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("PRIORITY FIX ORDER", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Fix Order (Based on ROI Impact)", margin, yPos);
    yPos += 12;

    // Priority list
    leaks.forEach((leak, index) => {
        // Priority badge
        const priorityColors = [COLORS.danger, COLORS.danger, COLORS.warning, COLORS.warning, COLORS.success, COLORS.success, COLORS.success];
        doc.setFillColor(...(priorityColors[index] || COLORS.textLight));
        doc.roundedRect(margin, yPos - 4, 8, 8, 1, 1, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}`, margin + 4, yPos + 1, { align: 'center' });

        // Title
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.text);
        doc.text(leak.title, margin + 14, yPos);

        // Difficulty
        drawDifficultyBadge(doc, leak.difficulty, pageWidth - margin - 30, yPos - 4);

        yPos += 12;
    });

    yPos += 10;

    // Explanation box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, yPos, contentWidth, 25, 3, 3, 'F');
    yPos += 8;
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'italic');
    doc.text("Fixing in this order maximizes return while minimizing technical complexity.", margin + 8, yPos);
    doc.text("Start with high-impact, low-effort fixes for quick wins.", margin + 8, yPos + 7);

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== 30-DAY ACTION PLAN PAGE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("30-DAY ACTION PLAN", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    const weeklyPlan = [
        {
            week: 1,
            title: "Capture & Retain",
            tasks: leaks.filter(l => l.weekAssignment === 1).map(l => l.title),
            focus: "Set up systems to capture and retain visitors"
        },
        {
            week: 2,
            title: "Increase Trust",
            tasks: leaks.filter(l => l.weekAssignment === 2).map(l => l.title),
            focus: "Build credibility and improve conversion triggers"
        },
        {
            week: 3,
            title: "Increase Order Value",
            tasks: leaks.filter(l => l.weekAssignment === 3).map(l => l.title),
            focus: "Maximize revenue per customer"
        },
        {
            week: 4,
            title: "Optimize Performance",
            tasks: leaks.filter(l => l.weekAssignment === 4).map(l => l.title),
            focus: "Technical improvements for better experience"
        }
    ];

    weeklyPlan.forEach((week, i) => {
        if (week.tasks.length === 0 && i > 0) return; // Skip empty weeks except week 1

        // Week header
        doc.setFillColor(...COLORS.primary);
        doc.roundedRect(margin, yPos, 50, 8, 2, 2, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.text(`WEEK ${week.week}`, margin + 25, yPos + 5.5, { align: 'center' });

        doc.setFontSize(11);
        doc.setTextColor(...COLORS.text);
        doc.text(week.title, margin + 55, yPos + 5);

        yPos += 12;

        // Focus area
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.textLight);
        doc.setFont('helvetica', 'italic');
        doc.text(`Focus: ${week.focus}`, margin + 5, yPos);
        yPos += 8;

        // Tasks
        if (week.tasks.length > 0) {
            week.tasks.forEach((task, j) => {
                doc.setFontSize(9);
                doc.setTextColor(...COLORS.text);
                doc.setFont('helvetica', 'normal');
                doc.text(`[ ] ${task}`, margin + 10, yPos);
                yPos += 6;
            });
        } else {
            doc.setFontSize(9);
            doc.setTextColor(...COLORS.success);
            doc.text("All systems in place!", margin + 10, yPos);
            yPos += 6;
        }

        yPos += 8;
    });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== REVENUE PROJECTION PAGE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("REVENUE PROJECTION", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 45;

    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Potential Revenue Gains", margin, yPos);
    yPos += 15;

    // Three projection cards
    const projections = [
        { label: "Conservative", percent: 10, color: COLORS.textLight },
        { label: "Moderate", percent: 20, color: COLORS.warning },
        { label: "Aggressive", percent: 30, color: COLORS.success }
    ];

    const cardWidth = (contentWidth - 20) / 3;
    projections.forEach((proj, i) => {
        const cardX = margin + (i * (cardWidth + 10));
        
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(cardX, yPos, cardWidth, 35, 3, 3, 'F');
        
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.textLight);
        doc.setFont('helvetica', 'bold');
        doc.text(proj.label.toUpperCase(), cardX + cardWidth / 2, yPos + 10, { align: 'center' });
        
        doc.setFontSize(18);
        doc.setTextColor(...proj.color);
        doc.text(`+${proj.percent}%`, cardX + cardWidth / 2, yPos + 25, { align: 'center' });
    });

    yPos += 55;

    // Example calculation box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, yPos, contentWidth, 50, 3, 3, 'F');
    doc.setDrawColor(...COLORS.success);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, contentWidth, 50, 3, 3, 'S');

    yPos += 10;
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Example Impact Calculation", margin + 8, yPos);

    yPos += 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const examples = [
        "If current monthly revenue = $5,000",
        "+20% (Moderate) = +$1,000 per month",
        "Annual Impact = +$12,000"
    ];
    
    examples.forEach((ex, i) => {
        doc.setTextColor(i === 2 ? COLORS.success[0] : COLORS.text[0], 
                        i === 2 ? COLORS.success[1] : COLORS.text[1], 
                        i === 2 ? COLORS.success[2] : COLORS.text[2]);
        doc.setFont('helvetica', i === 2 ? 'bold' : 'normal');
        doc.text(ex, margin + 8, yPos);
        yPos += 7;
    });

    yPos += 25;

    // Value callout
    doc.setFillColor(...COLORS.primary);
    doc.roundedRect(margin, yPos, contentWidth, 20, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("Your $19 investment could yield $1,000+ monthly returns when optimized correctly.", pageWidth / 2, yPos + 12, { align: 'center' });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== TOOLS & SOFTWARE RECOMMENDATIONS PAGE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("RECOMMENDED TOOLS", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    const tools = [
        { category: "Email Marketing", tools: ["Klaviyo (Best for ecommerce)", "Mailchimp (General purpose)"] },
        { category: "Reviews & Social Proof", tools: ["Judge.me", "Loox (Photo reviews)"] },
        { category: "Cart Recovery", tools: ["Klaviyo", "CartHook"] },
        { category: "Speed Testing", tools: ["Google PageSpeed Insights", "GTmetrix"] },
        { category: "Mobile Testing", tools: ["Google Mobile-Friendly Test", "BrowserStack"] },
        { category: "Upsells & Bundles", tools: ["Bold Product Options", "ReConvert"] }
    ];

    tools.forEach((cat, i) => {
        // Category header
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, yPos, contentWidth, 22, 2, 2, 'F');
        
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.text);
        doc.setFont('helvetica', 'bold');
        doc.text(cat.category, margin + 8, yPos + 7);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.textLight);
        doc.text(cat.tools.join("  |  "), margin + 8, yPos + 16);
        
        yPos += 28;
    });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== DIY VS DONE-FOR-YOU PAGE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("IMPLEMENTATION OPTIONS", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 45;

    // Comparison table header
    doc.setFillColor(...COLORS.dark);
    doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'F');
    
    const colWidths = [55, 35, 35, 45];
    let colX = margin + 5;
    
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    ['Option', 'Time', 'Risk', 'Results Speed'].forEach((header, i) => {
        doc.text(header, colX, yPos + 8);
        colX += colWidths[i];
    });

    yPos += 15;

    // Table rows
    const options = [
        { option: "DIY Implementation", time: "15-30 hrs", risk: "Medium", speed: "Slow (4-8 weeks)", riskColor: COLORS.warning },
        { option: "Professional Help", time: "3-7 days", risk: "Low", speed: "Fast (1-2 weeks)", riskColor: COLORS.success }
    ];

    options.forEach((row, i) => {
        doc.setFillColor(i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 250 : 255, i % 2 === 0 ? 252 : 255);
        doc.roundedRect(margin, yPos, contentWidth, 12, 0, 0, 'F');
        
        colX = margin + 5;
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.text);
        doc.setFont('helvetica', 'normal');
        
        doc.text(row.option, colX, yPos + 8);
        colX += colWidths[0];
        doc.text(row.time, colX, yPos + 8);
        colX += colWidths[1];
        doc.setTextColor(...row.riskColor);
        doc.text(row.risk, colX, yPos + 8);
        colX += colWidths[2];
        doc.setTextColor(...COLORS.text);
        doc.text(row.speed, colX, yPos + 8);
        
        yPos += 14;
    });

    yPos += 20;

    // CTA Box
    doc.setFillColor(...COLORS.primary);
    doc.roundedRect(margin, yPos, contentWidth, 50, 3, 3, 'F');
    
    yPos += 12;
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("Want This Fixed For You?", pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text("Our team can implement all fixes in 3-7 business days.", pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    // Button simulation with clickable link
    const buttonX = pageWidth / 2 - 40;
    const buttonY = yPos - 3;
    const buttonW = 80;
    const buttonH = 12;
    
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(buttonX, buttonY, buttonW, buttonH, 2, 2, 'F');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("Schedule Consultation", pageWidth / 2, yPos + 4, { align: 'center' });
    
    // Add clickable email link
    doc.link(buttonX, buttonY, buttonW, buttonH, { url: 'mailto:hello@sitegradepro.com?subject=Profit%20Leak%20Fix%20-%20Consultation%20Request' });

    yPos += 25;
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.text("Email: hello@sitegradepro.com", pageWidth / 2, yPos, { align: 'center' });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== CLOSING PAGE ==========
    doc.addPage();
    pageNum++;

    // Dark background for closing
    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Accent bar
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, pageHeight / 2 - 50, pageWidth, 3, 'F');

    // Main message
    doc.setFontSize(22);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    
    const closingLine1 = "Your Website Should Be";
    const closingLine2 = "Generating Revenue";
    const closingLine3 = "Not Leaking It.";
    
    doc.text(closingLine1, pageWidth / 2, pageHeight / 2 - 25, { align: 'center' });
    doc.setTextColor(...COLORS.secondary);
    doc.text(closingLine2, pageWidth / 2, pageHeight / 2 - 5, { align: 'center' });
    doc.setTextColor(...COLORS.white);
    doc.text(closingLine3, pageWidth / 2, pageHeight / 2 + 15, { align: 'center' });

    // Subtext
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'normal');
    const subtext = doc.splitTextToSize(
        "This report identifies structural optimization gaps based on your submitted responses. Strategic implementation can significantly improve conversion performance.",
        contentWidth - 20
    );
    doc.text(subtext, pageWidth / 2, pageHeight / 2 + 40, { align: 'center' });

    // Logo/branding
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", pageWidth / 2, pageHeight - 40, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'normal');
    doc.text("SiteGradePro.com", pageWidth / 2, pageHeight - 30, { align: 'center' });

    addPageNumber(doc, pageNum);

    return doc;
}

// Export function to download the PDF
export async function downloadProfitLeakFixPlan(data) {
    const doc = await generateProfitLeakFixPlanPDF(data);
    const fileName = `profit-leak-fix-plan-${data.leadData?.website?.replace(/[^a-z0-9]/gi, '-') || 'report'}.pdf`;
    doc.save(fileName);
    return fileName;
}
